import {
  AUDIT_ACTION_LABELS,
  type AuditAction,
  type TicketDiagnostics,
  type TicketOriginSource,
} from '@catalyst-psa/shared';

import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { Modal } from '../../components/ui/Modal.js';
import { formatChangeValue } from '../audit/HistoryPanel.js';
import { formatDateTime } from '../../utils/date.js';
import { useTicketDiagnostics } from './api.js';

interface TicketDiagnosticsModalProps {
  isOpen: boolean;
  ticketId: string | null;
  onClose: () => void;
}

const ORIGIN_TONES: Record<TicketOriginSource, 'blue' | 'green' | 'gray' | 'amber'> = {
  connectwise_sync: 'blue',
  clickup: 'green',
  psa: 'gray',
  unknown: 'amber',
};

const ORIGIN_LABELS: Record<TicketOriginSource, string> = {
  connectwise_sync: 'ConnectWise sync',
  clickup: 'ClickUp',
  psa: 'PSA',
  unknown: 'Unknown',
};

const ACTION_TONES: Record<AuditAction, 'green' | 'blue' | 'red'> = {
  create: 'green',
  update: 'blue',
  delete: 'red',
};

function Field({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 break-all font-mono text-xs text-slate-800">{value}</dd>
    </div>
  );
}

export function TicketDiagnosticsModal({
  isOpen,
  ticketId,
  onClose,
}: TicketDiagnosticsModalProps): JSX.Element {
  const query = useTicketDiagnostics(ticketId, isOpen);
  const data = query.data;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Technical history"
      size="lg"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <DataState
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        onRetry={() => void query.refetch()}
        isEmpty={!query.isLoading && !data}
        emptyTitle="No diagnostics"
        emptyDescription="This ticket could not be analyzed."
      >
        {data ? <Body data={data} /> : <div />}
      </DataState>
    </Modal>
  );
}

function Body({ data }: { data: TicketDiagnostics }): JSX.Element {
  return (
    <div className="flex flex-col gap-5">
      {/* Origin + number */}
      <section className="rounded-lg border border-slate-200 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-semibold text-slate-800">Origin</h3>
          <Badge tone={ORIGIN_TONES[data.originSource]}>{ORIGIN_LABELS[data.originSource]}</Badge>
        </div>
        <p className="mt-2 text-sm text-slate-700">{data.originExplanation}</p>
        <p className="mt-3 text-sm text-slate-700">{data.numberExplanation}</p>
      </section>

      {/* Possible duplicates */}
      {data.duplicates.length > 0 ? (
        <section className="rounded-lg border border-amber-300 bg-amber-50 p-4">
          <h3 className="text-sm font-semibold text-amber-800">
            Possible duplicates ({data.duplicates.length})
          </h3>
          <p className="mt-1 text-xs text-amber-700">
            Other tickets that look like the same record.
          </p>
          <ul className="mt-3 space-y-2">
            {data.duplicates.map((dup) => (
              <li
                key={dup.id}
                className="flex flex-wrap items-center gap-2 rounded-md bg-white p-2 text-sm ring-1 ring-amber-200"
              >
                <span className="font-medium tabular-nums text-slate-900">#{dup.number}</span>
                <Badge tone={dup.source === 'connectwise' ? 'blue' : 'gray'}>{dup.source}</Badge>
                <span className="min-w-0 flex-1 truncate text-slate-700">{dup.title}</span>
                <Badge tone="amber">{dup.matchReason}</Badge>
                <span className="text-xs text-slate-400">{formatDateTime(dup.createdAt)}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* Provenance */}
      <section className="rounded-lg border border-slate-200 p-4">
        <h3 className="text-sm font-semibold text-slate-800">Record provenance</h3>
        <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
          <Field label="PSA ticket number" value={String(data.number)} />
          <Field label="Source" value={data.source} />
          <Field label="ConnectWise id (cwId)" value={data.cwId ?? '—'} />
          <Field label="ClickUp task id" value={data.cuTaskId ?? '—'} />
          <Field label="Created" value={formatDateTime(data.createdAt)} />
          <Field label="Last updated" value={formatDateTime(data.updatedAt)} />
          <Field label="CW payload hash" value={data.cwPayloadHash ?? '—'} />
          <div className="sm:col-span-3">
            <dt className="text-xs uppercase tracking-wide text-slate-500">ClickUp task link</dt>
            <dd className="mt-1 text-xs">
              {data.cuTaskLink ? (
                <a
                  href={data.cuTaskLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="break-all font-medium text-brand-600 hover:text-brand-700"
                >
                  {data.cuTaskLink}
                </a>
              ) : (
                <span className="font-mono text-slate-800">—</span>
              )}
            </dd>
          </div>
        </dl>
      </section>

      {/* Audit timeline */}
      <section className="rounded-lg border border-slate-200 p-4">
        <h3 className="text-sm font-semibold text-slate-800">Change history</h3>
        {data.audit.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">
            No PSA audit events. (ConnectWise sync writes records directly and is not audited
            per-record — see the ConnectWise Sync activity log for run-level history.)
          </p>
        ) : (
          <ol className="mt-3 divide-y divide-slate-100">
            {data.audit.map((entry) => (
              <li key={entry.id} className="py-3 first:pt-0 last:pb-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={ACTION_TONES[entry.action]}>
                    {AUDIT_ACTION_LABELS[entry.action]}
                  </Badge>
                  <span className="text-sm font-medium text-slate-900">{entry.actorName}</span>
                  <span className="text-xs text-slate-400">{formatDateTime(entry.occurredAt)}</span>
                </div>
                {entry.changes.length > 0 ? (
                  <ul className="mt-2 space-y-1">
                    {entry.changes.map((change) => (
                      <li
                        key={change.field}
                        className="flex flex-wrap items-baseline gap-x-2 text-sm"
                      >
                        <span className="font-medium text-slate-600">{change.label}:</span>
                        <span className="text-slate-400 line-through">
                          {change.before !== null ? formatChangeValue(change.before) : '—'}
                        </span>
                        <span aria-hidden className="text-slate-400">
                          →
                        </span>
                        <span className="text-slate-800">
                          {change.after !== null ? formatChangeValue(change.after) : '—'}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}
